import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { DataTable, ErrorState, PageContainer, PageHeader, TableSkeleton } from '../../../components/ui/Page';
import { useToast } from '../../../components/ui/Toast';
import { useAuth } from '../../auth/AuthProvider';
import type { Scope } from '../../auth/types';
import { SCOPE_LABELS, teamApi, teamKeys, type PermissionDef, type RoleDetail } from '../api';

const SCOPE_RANK: Record<Scope, number> = { own: 1, assigned: 2, team: 3, branch: 4, tenant: 5, platform: 6 };

const MODULE_LABELS: Record<string, string> = {
  tenant: 'Business account',
  branding: 'Branding',
  branches: 'Branches',
  departments: 'Teams',
  staff: 'Staff',
  roles: 'Roles',
  leads: 'Leads',
  lead_config: 'Lead settings',
  pipelines: 'Pipelines',
  forms: 'Lead forms',
  assignments: 'Assignment',
  transfers: 'Transfers',
  activities: 'Activities',
  followups: 'Follow-ups',
  notifications: 'Notifications',
  notification_rules: 'Automation',
  templates: 'Templates',
  messages: 'Customer messages',
  reports: 'Reports',
  integrations: 'Integrations',
  audit: 'Audit',
};

export function RolesListPage() {
  const navigate = useNavigate();
  const roles = useQuery({ queryKey: teamKeys.roles, queryFn: teamApi.roles });
  return (
    <PageContainer>
      <PageHeader
        title="Roles & permissions"
        description="Each role grants permissions at a scope: own, assigned, team, branch or everything."
      />
      <Card>
        {roles.isPending ? (
          <TableSkeleton />
        ) : roles.isError ? (
          <div className="p-4">
            <ErrorState error={roles.error} />
          </div>
        ) : (
          <DataTable
            rows={roles.data}
            rowKey={(r) => r.id}
            onRowClick={(r) => navigate(`/app/team/roles/${r.id}`)}
            empty={null}
            columns={[
              {
                header: 'Role',
                cell: (r) => (
                  <div>
                    <p className="font-medium text-fg">{r.name}</p>
                    <p className="text-xs text-fg-subtle">{r.description}</p>
                  </div>
                ),
              },
              { header: 'Members', cell: (r) => <span className="tabular-nums">{r.memberCount}</span> },
              {
                header: 'Permissions',
                cell: (r) => <span className="tabular-nums">{r.permissionCount}</span>,
              },
              { header: 'Rank', cell: (r) => <Badge>{r.rank}</Badge> },
            ]}
          />
        )}
      </Card>
    </PageContainer>
  );
}

/** Permission matrix for one role (docs/design/15 §3.8): scope per permission, limited by the rank rule. */
export function RoleDetailPage() {
  const { id = '' } = useParams();
  const role = useQuery({ queryKey: teamKeys.role(id), queryFn: () => teamApi.role(id) });
  const catalogue = useQuery({ queryKey: teamKeys.permissions, queryFn: teamApi.permissions });
  if (role.isPending || catalogue.isPending)
    return (
      <PageContainer>
        <TableSkeleton />
      </PageContainer>
    );
  if (role.isError || catalogue.isError)
    return (
      <PageContainer>
        <ErrorState error={role.error ?? catalogue.error} />
      </PageContainer>
    );
  // Remount when the saved permissions change so the draft starts from the server copy.
  return (
    <RoleMatrix
      key={JSON.stringify(role.data.permissions)}
      id={id}
      role={role.data}
      catalogue={catalogue.data}
    />
  );
}

function RoleMatrix({ id, role, catalogue }: { id: string; role: RoleDetail; catalogue: PermissionDef[] }) {
  const { me } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, Scope>>(role.permissions);

  const groups = useMemo(() => {
    const map = new Map<string, PermissionDef[]>();
    for (const p of catalogue) map.set(p.module, [...(map.get(p.module) ?? []), p]);
    return [...map.entries()];
  }, [catalogue]);

  const changes = useMemo(() => {
    const before = role.permissions;
    const keys = new Set([...Object.keys(before), ...Object.keys(draft)]);
    return [...keys].filter((k) => before[k] !== draft[k]).length;
  }, [draft, role.permissions]);

  const save = useMutation({
    mutationFn: () => teamApi.setRolePermissions(id, draft),
    onSuccess: async (data) => {
      queryClient.setQueryData(teamKeys.role(id), data);
      await queryClient.invalidateQueries({ queryKey: teamKeys.roles });
      toast.success('Role permissions saved');
    },
    onError: (err) => toast.error(err),
  });

  const editable = role.editable;
  const myScope = (code: string): Scope | undefined => me?.permissions[code]?.scope;

  return (
    <PageContainer>
      <PageHeader
        back={{ to: '/app/team/roles', label: 'Roles' }}
        title={role.name}
        description={role.description}
        actions={
          editable ? (
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={changes === 0}>
              {changes ? `Save ${changes} change${changes > 1 ? 's' : ''}` : 'No changes'}
            </Button>
          ) : (
            <span className="flex items-center gap-1.5 text-sm text-fg-muted">
              <Lock aria-hidden className="size-4" /> You can only edit roles ranked below yours
            </span>
          )
        }
      />
      <div className="space-y-4">
        {groups.map(([module, perms]) => (
          <Card key={module}>
            <h2 className="border-b border-line px-5 py-3 text-sm font-semibold text-fg">
              {MODULE_LABELS[module] ?? module}
            </h2>
            <ul className="divide-y divide-line">
              {perms.map((p) => {
                const mine = myScope(p.code);
                return (
                  <li key={p.code} className="flex flex-wrap items-center gap-3 px-5 py-2.5 text-sm">
                    <span className="min-w-56 flex-1 text-fg">{p.description}</span>
                    <select
                      aria-label={p.description}
                      value={draft[p.code] ?? ''}
                      disabled={!editable}
                      onChange={(e) =>
                        setDraft((d) => {
                          const next = { ...d };
                          if (e.target.value) next[p.code] = e.target.value as Scope;
                          else delete next[p.code];
                          return next;
                        })
                      }
                      className="h-8 rounded-md border border-line bg-surface px-2 text-sm disabled:opacity-60"
                    >
                      <option value="">No access</option>
                      {p.allowedScopes.map((s) => (
                        <option key={s} value={s} disabled={!mine || SCOPE_RANK[s] > SCOPE_RANK[mine]}>
                          {SCOPE_LABELS[s]}
                        </option>
                      ))}
                    </select>
                  </li>
                );
              })}
            </ul>
          </Card>
        ))}
      </div>
    </PageContainer>
  );
}
